import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Map as MapIcon, Plus, CalendarDays, Music2, Lock } from "lucide-react";
import { roadmapMe, roadmapList, roadmapCreate } from "@/lib/afmApi";
import { Loading, EmptyState, ErrorState } from "@/components/common";
import { KINDS, ProgressBar, fmtDate, daysUntil, todayIso } from "@/components/roadmap/shared";

const errText = (e) => e?.response?.data?.detail || "No se pudo completar";
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
const CURRENCIES = ["USD", "EUR", "VES", "COP", "MXN", "ARS", "CLP", "PEN", "DOP", "BRL", "GBP"];

export default function HojaDeRuta() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setFailed(false);
    Promise.all([roadmapMe(), roadmapList()])
      .then(([m, l]) => { setMe(m); setRows(l.roadmaps || []); })
      .catch(() => setFailed(true));
  };
  useEffect(load, []);

  if (failed) return <ErrorState onRetry={load} />;
  if (!me || !rows) return <Loading />;
  if (!me.enabled && !rows.length) {
    return <EmptyState icon={Lock} title="Hoja de ruta no activada" desc="Es la herramienta para planificar lanzamientos y carreras: calendario, fases por canción, presupuesto y recoup. Pídele a Always Fresh que la active para tu cuenta o tu sello." testId="rm-locked" />;
  }

  const startNew = () => {
    const sc = me.scopes[0] || {};
    setForm({ title: "", kind: "ep", scope: sc.kind || "user", manager_id: sc.manager_id || "", artist_id: sc.artists?.[0]?.id || "",
      artist_name: "", start_month: todayIso().slice(0, 7), months: 8, base_currency: "USD", songs: "" });
  };
  const scope = form && me.scopes.find((s) => s.kind === form.scope && (s.kind === "user" || s.manager_id === form.manager_id));
  const create = async () => {
    if (!form.title.trim()) { toast.error("Ponle un nombre"); return; }
    setBusy(true);
    try {
      const body = { ...form, songs: form.songs.split("\n").map((t) => t.trim()).filter(Boolean) };
      const rm = await roadmapCreate(body);
      toast.success("Hoja de ruta creada");
      navigate(`/hoja-de-ruta/${rm.id}`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="roadmap-list">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#CA0808]">Planificación</div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-black">Hoja de ruta</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Todo lo que hay que decidir, entregar y pagar de cada lanzamiento, en un sitio: calendario, fases por canción, presupuesto en cualquier moneda, ingresos y recoup.</p>
        </div>
        {me.scopes.length > 0 && !form && (
          <button onClick={startNew} className="flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white" data-testid="rm-new"><Plus className="h-4 w-4" /> Nueva hoja de ruta</button>
        )}
      </div>

      {form && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="rm-form">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500 md:col-span-2">Nombre
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="EP «American boy»" autoFocus />
            </label>
            <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo
              <select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            {me.scopes.length > 1 && (
              <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">De quién es
                <select className={inputCls} value={`${form.scope}|${form.manager_id}`} onChange={(e) => {
                  const [k, mid] = e.target.value.split("|");
                  const sc = me.scopes.find((s) => s.kind === k && (k === "user" || s.manager_id === mid));
                  setForm({ ...form, scope: k, manager_id: mid || "", artist_id: sc?.artists?.[0]?.id || "" });
                }}>
                  {me.scopes.map((s) => <option key={`${s.kind}|${s.manager_id || ""}`} value={`${s.kind}|${s.manager_id || ""}`}>{s.kind === "user" ? "Mi cuenta" : `Sello · ${s.label}`}</option>)}
                </select>
              </label>
            )}
            {form.scope === "manager" ? (
              <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Artista
                <select className={inputCls} value={form.artist_id} onChange={(e) => setForm({ ...form, artist_id: e.target.value })}>
                  {(scope?.artists || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
            ) : (
              <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Artista
                <input className={inputCls} value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} placeholder="Nombre artístico" />
              </label>
            )}
            <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Primer mes
              <input type="month" className={inputCls} value={form.start_month} onChange={(e) => setForm({ ...form, start_month: e.target.value })} />
            </label>
            <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Meses que dura
              <input type="number" min={1} max={24} className={inputCls} value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} />
            </label>
            <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Moneda principal
              <select className={inputCls} value={form.base_currency} onChange={(e) => setForm({ ...form, base_currency: e.target.value })}>
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="space-y-1 text-xs font-semibold uppercase tracking-wider text-slate-500 md:col-span-2">Canciones (una por línea, en orden)
              <textarea rows={4} className={inputCls} value={form.songs} onChange={(e) => setForm({ ...form, songs: e.target.value })} placeholder={"Lamiak\n7 meses\nRuta 66"} />
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={create} disabled={busy} className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Creando…" : "Crear"}</button>
            <button onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
          </div>
        </div>
      )}

      {rows.length === 0 && !form ? (
        <EmptyState icon={MapIcon} title="Aún no hay hojas de ruta" desc="Crea la primera: pon el nombre del proyecto y sus canciones. El calendario se arma solo con las fechas que vayas poniendo." testId="rm-empty" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((r) => {
            const d = daysUntil(r.next_date);
            return (
              <Link key={r.id} to={`/hoja-de-ruta/${r.id}`} className="group rounded-xl border border-slate-200 bg-white p-5 transition hover:border-black" data-testid={`rm-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{KINDS[r.kind] || "Proyecto"} · {r.artist_name || "—"}</div>
                    <h3 className="mt-1 truncate font-serif text-xl font-bold text-black">{r.title}</h3>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">{r.progress}%</span>
                </div>
                <ProgressBar value={r.progress} className="mt-3" />
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Music2 className="h-3.5 w-3.5" /> {r.songs} canción{r.songs === 1 ? "" : "es"}</span>
                  {r.next_date && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Próximo: {fmtDate(r.next_date)}{d !== null && d >= 0 ? ` (${d === 0 ? "hoy" : `en ${d} d`})` : ""}</span>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
